import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Copy, Check, ShieldCheck, QrCode } from 'lucide-react';
import { QRModal } from './QRModal';

const COINS = [
  {
    ticker: 'XMR',
    name: 'Monero',
    address: '82wJvyahbk7CvrWEo9g8emSCS9ij9VJ2V7eS8CgRp9k2CgqaXs92EvEBxBveYuVGapXKgshNmK7YoVp7Bcr6LCKhHBWapfo',
    colour: '#FF6600',
    note: 'privacy by default',
  },
  {
    ticker: 'ZEC',
    name: 'Zcash',
    address: 'u14ksnvyk3tf4qlts4wl4q0aulvn0ls6vs53dmrekc6vvclw7q2dzn46lcd4dq04da8whqx6e09zh343mz5mwt0e9a8gg42ljmu04zu738j84a80s4zpg6r069rvw40w2gn6v053z8wtnf4h7fm7jy4qg239ekhj2fn9pr7xlxcsv5rj5p',
    colour: '#F4B728',
    note: 'shielded address',
  },
  {
    ticker: 'ZANO',
    name: 'Zano',
    address: 'ZxCEMNSTkrk8ymoyewwTqtjZBGjRnqeTiexL1S5Z1zQML3BGNAvWGF3EfqtmkkXKgshNmK7YoVp7Bcr6LCKhHBWapfo',
    colour: '#00C4B8',
    note: 'confidential layer',
  },
  {
    ticker: 'ARRR',
    name: 'Pirate Chain',
    address: 'zs13ezxad98t494ty22rarjfr2ljuf9c44h34wz4540u57j3pnqzhrs7cm96flx4352nn7kk2dx5wa',
    colour: '#FFC700',
    note: 'fully shielded',
  },
];

function truncate(str, head = 14, tail = 10) {
  if (str.length <= head + tail + 3) return str;
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
}

function CoinRow({ coin, index, onShowQR }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(coin.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = coin.address;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="support-row"
      data-testid={`support-row-${coin.ticker.toLowerCase()}`}
    >
      {/* Ticker — clickable to open QR */}
      <div
        className="support-ticker-col cursor-pointer group"
        onClick={() => onShowQR(coin)}
        title={`Show QR for ${coin.ticker}`}
        data-testid={`qr-trigger-ticker-${coin.ticker.toLowerCase()}`}
      >
        <span
          className="support-ticker group-hover:underline transition-opacity"
          style={{ color: coin.colour }}
        >
          {coin.ticker}
        </span>
        <span className="support-coin-note">{coin.note}</span>
      </div>

      {/* Address — clickable to open QR */}
      <div
        className="support-address-col cursor-pointer"
        onClick={() => onShowQR(coin)}
        title="Click to show QR code"
        data-testid={`qr-trigger-address-${coin.ticker.toLowerCase()}`}
      >
        <span className="support-address-full">{coin.address}</span>
        <span className="support-address-short">{truncate(coin.address)}</span>
      </div>

      {/* QR icon button */}
      <button
        onClick={() => onShowQR(coin)}
        className="support-qr-btn"
        title="Show QR code"
        data-testid={`qr-btn-${coin.ticker.toLowerCase()}`}
        style={{ color: coin.colour }}
      >
        <QrCode size={13} />
      </button>

      {/* Copy button */}
      <button
        onClick={copy}
        className={`support-copy-btn ${copied ? 'support-copy-btn--done' : ''}`}
        title={copied ? 'Copied' : 'Copy address'}
        data-testid={`copy-btn-${coin.ticker.toLowerCase()}`}
      >
        {copied
          ? <><Check size={12} /><span>Copied</span></>
          : <><Copy size={12} /><span>Copy</span></>
        }
      </button>
    </motion.div>
  );
}

function SupportChannels({ qrCoin, setQrCoin }) {
  return (
    <>
      <div className="support-list" data-testid="support-list">
        {COINS.map((coin, i) => (
          <CoinRow key={coin.ticker} coin={coin} index={i} onShowQR={setQrCoin} />
        ))}
      </div>
      <p className="font-mono text-[10px] text-white/20 mt-6" data-testid="support-disclaimer">
        // Network-level privacy depends on the protocol itself — sending crypto does not, by itself, guarantee anonymity.
      </p>
      {qrCoin && <QRModal coin={qrCoin} onClose={() => setQrCoin(null)} />}
    </>
  );
}

export function SupportSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [revealed, setRevealed] = useState(false);
  const [qrCoin, setQrCoin] = useState(null);

  return (
    <section
      id="support"
      className="py-24 px-6 relative border-t border-white/5"
      data-testid="support-section"
      ref={ref}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 flex items-start gap-4"
        >
          <ShieldCheck size={16} className="text-[#FFC700] mt-1 shrink-0" />
          <div>
            <p className="font-mono text-xs text-[#FFC700] tracking-widest mb-1">
              // SUPPORT :: PRIVATE_CHANNELS
            </p>
            <h2 className="support-title">
              SUPPORT THE ARCHIVE
            </h2>
            <p className="font-mono text-xs text-white/25 mt-2">
              // Payments only. For communications, use SECURE CHANNEL above.
            </p>
          </div>
        </motion.div>

        {!revealed ? (
          <button
            onClick={() => setRevealed(true)}
            className="btn-ghost"
            data-testid="support-reveal-button"
          >
            REVEAL SUPPORT CHANNELS
          </button>
        ) : (
          <SupportChannels qrCoin={qrCoin} setQrCoin={setQrCoin} />
        )}
      </div>
    </section>
  );
}
